import {
    ChangeEvent,
    FC,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react'

import { Avatar } from '@/components'
import { AvatarCropModal } from '@/compositions'
import { isFetchResultSuccessful } from '@/entities/FetchResult'
import { uploadUserAvatarApiCall } from '@/entities/User/UserService'
import { trans } from '@/helpers/trans'
import { useTypedDispatch } from '@/redux/store'
import { setToast } from '@/redux/toasts/toastsReducer'
import { ToastType } from '@/types'

import './ProfileAvatar.scss'

interface ProfileAvatarProps {
    name: string
    image?: string
}

export const ProfileAvatar: FC<ProfileAvatarProps> = ({ name, image }): ReactElement => {
    const dispatch = useTypedDispatch()
    const inputRef = useRef<HTMLInputElement>(null)
    const [selectedImage, setSelectedImage] = useState<string>()
    const [uploadedImage, setUploadedImage] = useState<string>()
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => () => {
        if (selectedImage) {
            URL.revokeObjectURL(selectedImage)
        }
    }, [selectedImage])

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0]

        // Reset so picking the same file again still triggers a change
        event.target.value = ''

        if (file?.type.startsWith('image/')) {
            setSelectedImage(URL.createObjectURL(file))
        }
    }

    const handleCancel = (): void => setSelectedImage(undefined)

    const handleConfirm = async (avatar: Blob): Promise<void> => {
        setIsSaving(true)

        const result = await uploadUserAvatarApiCall(avatar)

        setIsSaving(false)

        if (! isFetchResultSuccessful(result)) {
            dispatch(setToast({ text: trans('pages.profile.avatar.uploadFailed'), type: ToastType.fail }))

            return
        }

        setUploadedImage(result.data)
        setSelectedImage(undefined)
        dispatch(setToast({ text: trans('pages.profile.avatar.uploadSuccess'), type: ToastType.pass }))
    }

    return (
        <div className="profile-avatar">
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="profile-avatar__button"
            >
                <Avatar
                    image={uploadedImage ?? image}
                    initials={name.charAt(0)}
                    name={name}
                    className="profile-avatar__avatar"
                />
                <span className="profile-avatar__label">{trans('pages.profile.avatar.change')}</span>
            </button>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="profile-avatar__input"
            />

            {selectedImage && (
                <AvatarCropModal
                    imageSrc={selectedImage}
                    isSaving={isSaving}
                    onCancel={handleCancel}
                    onConfirm={handleConfirm}
                />
            )}
        </div>
    )
}
